import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { topQueries } from "./data";
import { formatNumber } from "./validation";
import { useAuth } from "../../../hooks/AuthProvider";
import { AI_REPORT_INTENT_ANALYSIS } from "../../../config/urls";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#AF19FF",
  "#FF4D4F",
  "#13C2C2",
  "#722ED1",
];

const IntentAnalysisTab = ({ startDate, endDate }) => {
  const RADIAN = Math.PI / 180;

  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [intentDistribution, setIntentDistribution] = useState([]);
  const [intentPerformanceMetrics, setIntentPerformanceMetrics] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();

      const response = await fetch(
        `${AI_REPORT_INTENT_ANALYSIS}?from_date=${startDate}&to_date=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch report data");

      const json = await response.json();
      if (!json.success) throw new Error("API returned failure");

      const data = json.data;

      setIntentDistribution(data.intent_distribution || []);
      setIntentPerformanceMetrics(data.intent_performance_metrics || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        style={{ fontWeight: "bold", fontSize: "12px" }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Intent Distribution
        </Typography>
        <Box sx={{ height: 400, mt: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={intentDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderLabel}
                outerRadius={130}
                dataKey="value"
              >
                {intentDistribution.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Intent Performance Metrics
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Intent Type</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Count</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Percentage</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Avg Tokens</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Success Rate</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {intentPerformanceMetrics.map((row) => (
                <TableRow key={row.intent} hover>
                  <TableCell>
                    <Chip label={row.intent} size="small" color="info" />
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.count)}</TableCell>
                  <TableCell align="right">{row.percentage}%</TableCell>
                  <TableCell align="right">{row.average_tokens}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${row.avg_success_rate}%`}
                      size="small"
                      color={row.avg_success_rate >= 95 ? "success" : "warning"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Top User Queries
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>#</strong>
                </TableCell>
                <TableCell>
                  <strong>Query Pattern</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Frequency</strong>
                </TableCell>
                <TableCell>
                  <strong>Intent</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Avg Response Time</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topQueries.map((row) => (
                <TableRow key={row.rank} hover>
                  <TableCell>{row.rank}</TableCell>
                  <TableCell>{row.query}</TableCell>
                  <TableCell align="right">
                    {formatNumber(row.frequency)}
                  </TableCell>
                  <TableCell>
                    <Chip label={row.intent} size="small" color="info" />
                  </TableCell>
                  <TableCell align="right">{row.avgResponseTime}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default IntentAnalysisTab;
